/* COLESICE theme — core script.
   Loaded once in the <head> as a module. Section scripts (component-*.js, section-*.js) are modules too and run
   after this file, so they can use window.Theme (helpers, cart API, events) directly.

   Events dispatched on document:
     cart:change   { cart, source, sections }   after the cart changed through Theme.cart
     cart:refresh  {}                           ask cart UIs to reload themselves (e.g. after an app changed the cart)
     variant:change { sectionId, variant }      dispatched by product forms
     dialog:open / dialog:close { dialog }      when a <dialog> opens or closes
*/

const Theme = (window.Theme = window.Theme || {});
const config = window.theme || { routes: { root: '/' }, strings: {} };
Theme.config = config;
// The wide-screen zoom is retired (round owner-7, notes/wide/SCALE.md): CSS px = screen px. Kept so older code that
// divides a rect by window.csZ() stays right.
window.csZ = () => 1;
// the scrollbar space reserved by Theme.openDialog, given back once no modal dialog is open
Theme.releaseGutter = () => { const r = document.documentElement; if (r.dataset.csGutter) { r.style.removeProperty('scrollbar-gutter'); delete r.dataset.csGutter; } };

/* ---------- Helpers ---------- */
Theme.debounce = (fn, wait = 250) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

Theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

// Fetches a page with only the given sections rendered (Section Rendering API). Returns { id: html }.
Theme.fetchSections = async (url, sectionIds) => {
  const u = new URL(url, location.href);
  u.searchParams.set('sections', [].concat(sectionIds).join(','));
  const res = await fetch(u, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Section request failed (${res.status})`);
  return res.json();
};

// Fetches a page as a document, optionally only one section (section_id).
Theme.fetchDocument = async (url, sectionId) => {
  const u = new URL(url, location.href);
  if (sectionId) u.searchParams.set('section_id', sectionId);
  const res = await fetch(u);
  return Theme.parseHTML(await res.text());
};

// Replaces the inner HTML of every element matching selector with the matching element of a new document.
Theme.replaceContent = (fromDoc, selector, root = document) => {
  const next = fromDoc.querySelector(selector);
  const current = root.querySelector(selector);
  if (next && current) current.innerHTML = next.innerHTML;
  return current;
};

// Money drawn by script. The pattern comes from Liquid (config.moneySample = 1,234,567.89 formatted by Shopify in the
// visitor's own currency), so the symbol and separators are exactly what the shelf prices show. Never a fixed currency.
let moneyPattern;
const readMoneySample = (sample) => {
  const m = typeof sample === 'string' && sample.match(/^(.*?)(\d[\d.,'\u2019\u00a0\u202f ]*\d)(.*)$/);
  if (!m) return null;
  const [, prefix, num, suffix] = m;
  const seps = num.replace(/\d/g, '');
  const last = seps.slice(-1);
  const tail = num.slice(num.lastIndexOf(last) + 1);
  const hasDecimals = !!last && tail.length === 2;
  const decimal = hasDecimals ? last : '';
  const thousands = (hasDecimals ? seps.slice(0, -1) : seps).charAt(0);
  return { prefix, suffix, decimal, thousands, precision: hasDecimals ? 2 : 0 };
};
Theme.formatMoney = (cents, format) => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  cents = Number(cents) || 0;
  const delimit = (n, precision, thousands = ',', decimal = '.') => {
    const [whole, frac] = (n / 100).toFixed(precision).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  if (!format) {
    if (moneyPattern === undefined) moneyPattern = readMoneySample(config.moneySample);
    if (moneyPattern) return moneyPattern.prefix + delimit(cents, moneyPattern.precision, moneyPattern.thousands, moneyPattern.decimal) + moneyPattern.suffix;
    const active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency;
    if (active) {
      try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: active }).format(cents / 100); } catch (e) { /* unknown code */ }
      return `${delimit(cents, 2)} ${active}`;
    }
    format = config.moneyFormat || '{{amount}}';
  }
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return delimit(cents, 2);
      case 'amount_no_decimals': return delimit(cents, 0);
      case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
      case 'amount_with_space_separator': return delimit(cents, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return delimit(cents, 0, ' ');
      case 'amount_with_period_and_space_separator': return delimit(cents, 2, ' ', '.');
      default: return delimit(cents, 2);
    }
  });
};

Theme.emit = (name, detail = {}) => document.dispatchEvent(new CustomEvent(name, { detail }));

/* ---------- Cart API ---------- */
// Every element with data-cart-section="<section id>" is re-rendered after a cart change.
const cartSectionIds = () => [...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection))];

let ownCartRequest = 0;
async function cartRequest(url, body, source) {
  ownCartRequest++;
  try {
    const sections = cartSectionIds();
    const isForm = body instanceof FormData;
    if (sections.length) {
      if (isForm) { body.append('sections', sections.join(',')); body.append('sections_url', location.pathname); }
      else Object.assign(body, { sections: sections.join(','), sections_url: location.pathname });
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });
    let data;
    try { data = await res.json(); } catch (e) { data = { status: res.status, description: config.strings.cartError }; } // HTML error page (429, 5xx)
    if (!res.ok || data.status) {
      const error = new Error(data.description || data.message || config.strings.cartError || '');
      error.data = data;
      throw error;
    }
    // R9-U (lead, 4 Nov): the returned sections are drawn first; the GET /cart.js that an add needs for the event
    // detail is no longer on the critical path of the bag update
    Theme.renderCartSections(data.sections);
    const cart = data.items && data.token ? data : await Theme.cart.get();
    Theme.emit('cart:change', { cart, source, sections: data.sections || {}, response: data });
    return data;
  } finally {
    setTimeout(() => ownCartRequest--, 50);
  }
}

Theme.cart = {
  get: () => fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
  add: (formDataOrItems, source = 'cs-buy-form') => {
    const body = formDataOrItems instanceof FormData ? formDataOrItems : { items: [].concat(formDataOrItems) };
    return cartRequest(`${config.routes.cartAdd}.js`, body, source);
  },
  change: (line, quantity, source = 'cart') => cartRequest(`${config.routes.cartChange}.js`, { line, quantity }, source),
  update: (payload, source = 'cart') => cartRequest(`${config.routes.cartUpdate}.js`, payload, source),
};

// Puts freshly rendered section HTML (from a cart response) into the page.
Theme.renderCartSections = (sections) => {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = Theme.parseHTML(html);
    document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((target) => {
      const key = target.dataset.cartPart;
      const source = key ? doc.querySelector(`[data-cart-part="${key}"]`) : doc.querySelector(`[data-cart-section="${id}"]`);
      if (source) target.innerHTML = source.innerHTML;
    });
  }
};

// Apps that change the cart with their own requests: ask the cart UIs to refresh.
(function watchForeignCartChanges() {
  const isCartUrl = (url) => /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/.test(String(url));
  const ping = Theme.debounce(() => { if (!ownCartRequest) Theme.emit('cart:refresh'); }, 150);
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const own = ownCartRequest > 0; // requests made by Theme.cart itself are already rendered
    const p = origFetch.apply(this, args);
    try { const url = args[0] instanceof Request ? args[0].url : args[0]; if (!own && isCartUrl(url)) p.then(ping, () => {}); } catch (e) {}
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isCartUrl(url) && !ownCartRequest) this.addEventListener('load', ping);
    return origOpen.call(this, method, url, ...rest);
  };
})();

// Cart count bubbles anywhere on the page: <span data-cart-count>3</span>; [data-cart-text="Bag ([count])"] labels;
// [data-aria-one] / [data-aria-other] aria-label templates.
// [data-cart-count-wrap] (the "(n)" after Bag) hides with a zero count; a number inside it is never hidden on its own.
const setCartCount = (count) => {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = !count && !el.closest('[data-cart-count-wrap]');
  });
  document.querySelectorAll('[data-cart-count-wrap]').forEach((el) => { el.hidden = !count; });
  // Board header "Bag (1)": the whole label is one text, rebuilt from its template ("Bag ([count])")
  document.querySelectorAll('[data-cart-text]').forEach((el) => { el.textContent = el.dataset.cartText.replace('[count]', count); });
  document.querySelectorAll('[data-aria-one]').forEach((el) => {
    const t = count === 1 ? el.dataset.ariaOne : el.dataset.ariaOther;
    if (t) el.setAttribute('aria-label', t.replace('[count]', count));
  });
};
document.addEventListener('cart:change', (e) => { if (e.detail.cart) setCartCount(e.detail.cart.item_count); });
document.addEventListener('cart:refresh', () => Theme.cart.get().then((cart) => setCartCount(cart.item_count)));

/* ---------- Dialogs (drawers, modals) ---------- */
// <button data-dialog-open="cart-drawer"> opens <dialog id="cart-drawer">; [data-dialog-close] closes the nearest dialog.
const lastFocus = new WeakMap();
// Keyboard users get focus on the first control (with its focus ring); pointer users get focus on the dialog box itself,
// so no ring shows around the close button when a drawer opens from a click or tap (live behaviour).
let keyboardInput = false;
document.addEventListener('keydown', () => { keyboardInput = true; }, true);
document.addEventListener('pointerdown', () => { keyboardInput = false; }, true);
Theme.openDialog = (dialog, opener) => {
  if (typeof dialog === 'string') dialog = document.getElementById(dialog);
  if (!dialog || dialog.open) return;
  lastFocus.set(dialog, opener || document.activeElement);
  if (dialog.classList.contains('popover-dialog')) dialog.show();
  // speed (INP): lock the page before showModal, so the browser recalculates styles once instead of twice on the tap
  else {
    // dialogs marked data-dim-below-header (desktop country picker) leave the header lit, as the board does
    if (dialog.hasAttribute('data-dim-below-header')) {
      const bar = document.querySelector('.hd-bar');
      // getBoundingClientRect is in screen px; on a scaled wide screen (snippets/cs-scale) CSS px = screen px / csZ()
      if (bar) dialog.style.setProperty('--dim-top', `${Math.max(0, Math.round(bar.getBoundingClientRect().bottom / (window.csZ ? window.csZ() : 1)))}px`);
    }
    // no sideways jump (round owner-7): a classic scrollbar's space stays reserved while the lock hides it (same inline
    // value component-quick-add.js sets; set while the scrollbar is still there, so it moves nothing; nothing to do with
    // overlay / hidden scrollbars)
    const root = document.documentElement;
    if (window.innerWidth > root.clientWidth && !root.style.scrollbarGutter) { root.style.scrollbarGutter = 'stable'; root.dataset.csGutter = '1'; }
    document.body.classList.add('is-locked'); dialog.showModal();
  }
  if (!keyboardInput) {
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  }
  Theme.emit('dialog:open', { dialog });
};
Theme.closeDialog = (dialog) => {
  if (typeof dialog === 'string') dialog = document.getElementById(dialog);
  if (!dialog || !dialog.open) return;
  dialog.close();
};
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (opener) {
    const target = document.getElementById(opener.dataset.dialogOpen);
    if (target) { event.preventDefault(); Theme.openDialog(target, opener); }
    return;
  }
  const closer = event.target.closest('[data-dialog-close]');
  if (closer) { if (closer.tagName === 'A') event.preventDefault(); Theme.closeDialog(closer.closest('dialog')); return; }
  // click on the backdrop (outside the dialog box)
  if (event.target instanceof HTMLDialogElement && event.target.open && !event.target.classList.contains('popover-dialog')) {
    const r = event.target.getBoundingClientRect();
    const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
    if (!inside || event.target.classList.contains('drawer--full')) Theme.closeDialog(event.target);
  }
});
// a dialog-opener tap that came before this file ran (layout keeps it in window.__csPendingOpen) is replayed at the end of
// this file, once every click listener is bound (see "Early dialog taps" below)
document.addEventListener('close', (event) => {
  const dialog = event.target;
  if (!(dialog instanceof HTMLDialogElement)) return;
  if (!document.querySelector('dialog[open]:not(.popover-dialog)')) { document.body.classList.remove('is-locked'); Theme.releaseGutter(); }
  const back = lastFocus.get(dialog);
  if (back && back.isConnected) back.focus({ preventScroll: true });
  Theme.emit('dialog:close', { dialog });
}, true);

/* ---------- Quantity stepper ---------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-step]');
      if (!btn || !this.input) return;
      e.preventDefault();
      const step = Number(btn.dataset.step);
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step));
      if (String(next) !== this.input.value) {
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}
if (!customElements.get('cs-qty')) customElements.define('cs-qty', QuantityInput);

/* ---------- Carousel ---------- */
// <cs-carousel> wraps a .scroller track ([data-track]) of slides. Optional inside: [data-prev], [data-next],
// [data-dots] (filled automatically). Attributes: autoplay="6" (seconds), loop, align="center".
class ScrollCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]') || this.firstElementChild;
    if (!this.track) return;
    this.prevButtons = [...this.querySelectorAll('[data-prev]')];
    this.nextButtons = [...this.querySelectorAll('[data-next]')];
    this.dots = this.querySelector('[data-dots]');
    this.prevButtons.forEach((b) => b.addEventListener('click', () => this.go(-1)));
    this.nextButtons.forEach((b) => b.addEventListener('click', () => this.go(1)));
    this.onScroll = Theme.debounce(() => this.update(), 60);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => { this.buildDots(); this.update(); });
    this.resizeObserver.observe(this.track);
    this.buildDots();
    this.update();
    const seconds = Number(this.getAttribute('autoplay'));
    if (seconds > 0 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => { if (!this.paused && this.isInView) this.go(1, true); }, seconds * 1000);
      ['pointerenter', 'focusin', 'touchstart'].forEach((e) => this.addEventListener(e, () => (this.paused = true), { passive: true }));
      ['pointerleave', 'focusout'].forEach((e) => this.addEventListener(e, () => (this.paused = false)));
      this.io = new IntersectionObserver(([entry]) => (this.isInView = entry.isIntersecting));
      this.io.observe(this);
    }
  }
  disconnectedCallback() {
    clearInterval(this.timer);
    this.resizeObserver?.disconnect();
    this.io?.disconnect();
  }
  get slides() { return [...this.track.children].filter((el) => el.offsetParent !== null || el.getClientRects().length); }
  get pageCount() {
    const { scrollWidth, clientWidth } = this.track;
    const slide = this.slides[0];
    if (!slide || scrollWidth <= clientWidth + 2) return 1;
    const per = Math.max(1, Math.round(clientWidth / slide.getBoundingClientRect().width));
    return Math.max(1, this.slides.length - per + 1);
  }
  get index() {
    const left = Math.abs(this.track.scrollLeft);
    const slides = this.slides;
    let best = 0, bestDist = Infinity;
    const start = slides[0] ? slides[0].offsetLeft : 0;
    slides.forEach((s, i) => { const d = Math.abs(s.offsetLeft - start - left); if (d < bestDist) { bestDist = d; best = i; } });
    return best;
  }
  goTo(i, smooth = true) {
    const slides = this.slides;
    if (!slides.length) return;
    const n = Math.max(0, Math.min(i, slides.length - 1));
    const start = slides[0].offsetLeft;
    const dir = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
    this.track.scrollTo({ left: dir * (slides[n].offsetLeft - start), behavior: smooth ? 'smooth' : 'auto' });
  }
  go(step, wrap = this.hasAttribute('loop')) {
    let next = this.index + step;
    const last = this.pageCount - 1;
    if (next > last) next = wrap ? 0 : last;
    if (next < 0) next = wrap ? last : 0;
    this.goTo(next);
  }
  buildDots() {
    if (!this.dots) return;
    const count = this.pageCount;
    if (this.dots.childElementCount === count) return;
    this.dots.innerHTML = '';
    this.dots.hidden = count < 2;
    for (let i = 0; i < count; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dots__dot';
      b.setAttribute('aria-label', `${i + 1} / ${count}`);
      b.addEventListener('click', () => this.goTo(i));
      this.dots.append(b);
    }
  }
  update() {
    const i = this.index;
    const last = this.pageCount - 1;
    const loop = this.hasAttribute('loop');
    this.prevButtons.forEach((b) => (b.disabled = !loop && i <= 0));
    this.nextButtons.forEach((b) => (b.disabled = !loop && i >= last));
    this.dots?.querySelectorAll('.dots__dot').forEach((d, n) => d.setAttribute('aria-current', String(n === Math.min(i, last))));
    this.dispatchEvent(new CustomEvent('carousel:change', { detail: { index: i } }));
  }
}
if (!customElements.get('cs-carousel')) customElements.define('cs-carousel', ScrollCarousel);

/* ---------- Country / language picker ---------- */
// <localization-form> wraps Shopify's localization form; any [data-value] button or select change submits it.
class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.addEventListener('click', (e) => {
      const option = e.target.closest('[data-value]');
      if (!option || !this.form) return;
      e.preventDefault();
      const input = this.form.querySelector(`input[name="${option.dataset.name}"]`);
      if (input) { input.value = option.dataset.value; this.form.submit(); }
    });
    this.addEventListener('change', (e) => { if (e.target.matches('select')) this.form?.submit(); });
  }
}
if (!customElements.get('localization-form')) customElements.define('localization-form', LocalizationForm);

/* ---------- Entrance animations ---------- */
// [data-reveal] fades in when it enters the screen; product cards get a growing delay when "stagger" is on.
// Headings with [data-animate-heading] are split in lines and use the "Heading entrance" setting.
const headingMode = document.documentElement.dataset.headingAnimation || 'none';
// Stagger (live): the cards that come into view together form one batch; each gets its place in the batch
// (--reveal-index, in reading order) and the batch size (--reveal-count), which drive the delay curve in base.css.
const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  const shown = entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target);
  const batch = shown.filter((el) => el.hasAttribute('data-stagger'))
    .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  batch.forEach((el, i) => { el.style.setProperty('--reveal-index', i); el.style.setProperty('--reveal-count', batch.length); });
  shown.forEach((el) => {
    el.classList.add('is-visible');
    revealObserver.unobserve(el);
  });
}, { rootMargin: '0px 0px -40px 0px' }) : null;
// Live reveals a whole product row/grid at once: when the list (the cards' parent) comes 50px into view, all its
// cards fade in as one batch (index = place in the list, count = cards in the list), on screen or not.
const revealGroup = (list) => {
  const batch = [...list.children].filter((el) => el.hasAttribute('data-stagger') && !el.classList.contains('is-visible'));
  batch.forEach((el, i) => {
    el.style.setProperty('--reveal-index', i);
    el.style.setProperty('--reveal-count', batch.length);
    el.classList.add('is-visible');
    revealObserver.unobserve(el);
  });
  list.dataset.staggerGroup = 'done';
};
const groupObserver = revealObserver ? new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    groupObserver.unobserve(entry.target);
    revealGroup(entry.target);
  });
}, { rootMargin: '-50px 0px' }) : null;

function splitLines(el) {
  if (el.dataset.split) return;
  el.dataset.split = '1';
  const words = el.textContent.trim().split(/\s+/);
  if (!words.length || el.children.length) return; // keep headings with inner markup as they are
  el.textContent = '';
  const spans = words.map((w, i) => {
    const s = document.createElement('span');
    s.textContent = w + (i < words.length - 1 ? ' ' : '');
    el.append(s);
    return s;
  });
  const lines = [];
  let top = null;
  spans.forEach((s) => { if (s.offsetTop !== top) { lines.push([]); top = s.offsetTop; } lines[lines.length - 1].push(s.textContent); });
  el.textContent = '';
  lines.forEach((words2, i) => {
    const line = document.createElement('span');
    line.className = 'split-line';
    line.style.setProperty('--line-index', i);
    line.textContent = words2.join('');
    el.append(line);
  });
}

Theme.initAnimations = (root = document) => {
  if (!revealObserver || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el, i) => {
    el.classList.add('reveal');
    if (document.body.classList.contains('stagger-products') && el.matches('.product-tile, [data-reveal="stagger"]')) {
      el.setAttribute('data-stagger', '');
      const list = el.parentElement;
      // Cards added later to a list that already played (e.g. more results) reveal on their own, batched per callback.
      if (list && list.dataset.staggerGroup !== 'done') {
        if (!list.dataset.staggerGroup) { list.dataset.staggerGroup = 'wait'; groupObserver.observe(list); }
        return;
      }
    }
    revealObserver.observe(el);
  });
  if (headingMode === 'none') return;
  root.querySelectorAll('[data-animate-heading]:not([data-split])').forEach((el) => {
    if (window.Shopify && window.Shopify.designMode) return;
    splitLines(el);
    el.classList.add('split-heading', `split-heading--${headingMode}`);
    revealObserver.observe(el);
  });
};
document.addEventListener('DOMContentLoaded', () => Theme.initAnimations());
document.addEventListener('shopify:section:load', (e) => Theme.initAnimations(e.target));
document.addEventListener('tt:navigated', () => Theme.initAnimations());

/* ---------- Header height for sticky offsets ---------- */
Theme.setHeaderHeight = () => {
  const header = document.querySelector('[data-site-header]');
  const bar = document.querySelector('[data-announcement-bar]');
  const z = (window.csZ ? window.csZ() : 1); // scaled wide screen (snippets/cs-scale): CSS px = screen px / zoom
  if (header) document.documentElement.style.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height / z)}px`);
  document.documentElement.style.setProperty('--announcement-height', bar ? `${Math.round(bar.getBoundingClientRect().height / z)}px` : '0px');
};
window.addEventListener('resize', Theme.debounce(Theme.setHeaderHeight, 100));
document.addEventListener('DOMContentLoaded', Theme.setHeaderHeight);
document.addEventListener('shopify:section:load', Theme.setHeaderHeight);
// Theme editor: a section reloaded while its dialog was open leaves no close event behind; unlock the page.
document.addEventListener('shopify:section:unload', () => setTimeout(() => {
  if (!document.querySelector('dialog[open]:not(.popover-dialog)')) { document.body.classList.remove('is-locked'); Theme.releaseGutter(); }
}, 0));

/* ---------- Theme editor: open dialogs when their section is selected ---------- */
document.addEventListener('shopify:section:select', (e) => {
  const dialog = e.target.querySelector('dialog[data-editor-open]');
  if (dialog) Theme.openDialog(dialog);
});
document.addEventListener('shopify:section:deselect', (e) => {
  const dialog = e.target.querySelector('dialog[data-editor-open]');
  if (dialog) Theme.closeDialog(dialog);
});

/* ---------- Load on first use (speed, 2 Nov) ----------
   Scripts nothing needs at first paint stay out of the page load:
     <script type="text/cs-lazy" data-src="{{ 'x.js' | asset_url }}" data-when="idle interact visible" data-target="<selector>"></script>
   The module loads at the first of: the page idle after load (idle), a finger or pointer on / near data-target
   (interact: pointerover, pointerdown, touchstart, focusin), data-target within 600px of the screen (visible).
   A click on data-target that lands before the module is there is held and replayed once it has loaded, so no tap is lost. */
const lazyModules = new Map();
Theme.loadModule = (src) => {
  if (!lazyModules.has(src)) lazyModules.set(src, import(src).catch((error) => { lazyModules.delete(src); throw error; }));
  return lazyModules.get(src);
};
const lazyBound = new Set();
const bindLazy = () => document.querySelectorAll('script[type="text/cs-lazy"][data-src]').forEach((tag) => {
  const src = tag.dataset.src;
  if (lazyBound.has(src)) return;
  lazyBound.add(src);
  const target = tag.dataset.target || '';
  const when = (tag.dataset.when || 'idle').split(/\s+/);
  let ready = false;
  const go = () => Theme.loadModule(src).then((m) => { ready = true; return m; });
  if (when.includes('idle')) {
    // idle loads go at low priority (modulepreload fetchpriority=low first), so they never show up as critical requests
    const quiet = () => {
      if (!lazyModules.has(src) && !document.querySelector(`link[rel="modulepreload"][href="${src}"]`)) {
        const link = document.createElement('link');
        link.rel = 'modulepreload';
        link.fetchPriority = 'low';
        link.href = src;
        document.head.appendChild(link);
      }
      return go();
    };
    const idle = () => (window.requestIdleCallback ? requestIdleCallback(() => quiet().catch(() => {}), { timeout: 4000 }) : setTimeout(() => quiet().catch(() => {}), 2000));
    if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
  }
  if (!target) return;
  if (when.includes('visible') && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { io.disconnect(); go().catch(() => {}); }
    }, { rootMargin: '600px 0px' });
    document.querySelectorAll(target).forEach((el) => io.observe(el));
  }
  if (when.includes('interact')) {
    const near = (event) => { if (!ready && event.target.closest && event.target.closest(target)) go().catch(() => {}); };
    ['pointerover', 'pointerdown', 'touchstart', 'focusin'].forEach((type) => document.addEventListener(type, near, { passive: true, capture: true }));
    document.addEventListener('click', (event) => {
      if (ready) return;
      const el = event.target.closest && event.target.closest(target);
      if (!el) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      go().then(() => el.click(), () => { if (el.href) location.assign(el.href); });
    }, true);
  }
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindLazy, { once: true }); else bindLazy();
document.addEventListener('shopify:section:load', bindLazy);

/* ---------- Section scripts after the first paint (speed round 4, 2 Nov) ----------
   Sections print <script type="text/cs-defer" data-src="{{ 'x.js' | asset_url }}"> instead of a module <script src>, so
   none of them is requested before the page's main image is on screen. The layout starts their downloads
   (modulepreload) once the first contentful paint is reported; here they run in page order, as the module tags did.
   A tap on a control whose own section scripts (or custom element) are not ready yet is held and replayed the moment
   they are, so no tap is lost and no tap waits for unrelated scripts; a radio / checkbox choice changes at once and
   its change event is sent again when its scripts are there. Plain links and dialog openers are never held. */
const deferPreloaded = new Set();
const deferDone = new Map(); // src -> { promise, resolve }
const doneFor = (src) => {
  if (!deferDone.has(src)) { let resolve; const promise = new Promise((r) => { resolve = r; }); deferDone.set(src, { promise, resolve, ran: false }); }
  return deferDone.get(src);
};
let deferChain = Promise.resolve();
Theme.runDeferred = (root = document) => {
  const srcs = [...new Set([...root.querySelectorAll('script[type="text/cs-defer"][data-src]')].map((tag) => tag.dataset.src))]
    .filter((src) => !deferPreloaded.has(src));
  srcs.forEach((src) => {
    deferPreloaded.add(src);
    if (document.querySelector(`link[rel="modulepreload"][href="${src}"]`)) return; // already fetching (layout, after first paint)
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = src;
    document.head.appendChild(link);
  });
  srcs.forEach((src) => {
    const d = doneFor(src);
    deferChain = deferChain.then(() => Theme.loadModule(src).catch(() => {})).then(() => { d.ran = true; d.resolve(); });
  });
  return deferChain;
};
let deferStarted = false;
const startDeferred = () => {
  if (deferStarted) return;
  deferStarted = true;
  Theme.runDeferred();
};
// what a control needs before a tap on it can be handled: the scripts of its own section and its custom elements
const tapNeeds = (el) => {
  const waits = [];
  const host = el.closest('.shopify-section, #cs-hd-overlays');
  // R9-U (QA D-01, 4 Nov): a section re-rendered after a cart change can bring a script tag that was never queued (the
  // bag's component-quick-add.js after "Add both"): its promise never resolved, so every tap in the bag was held for
  // good. Queue the host's own scripts first (already-run ones are skipped), so a held tap is always replayed.
  if (host && deferStarted) Theme.runDeferred(host);
  if (host) host.querySelectorAll('script[type="text/cs-defer"][data-src]').forEach((tag) => { const d = doneFor(tag.dataset.src); if (!d.ran) waits.push(d.promise); });
  for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
    const name = node.localName;
    if (name.includes('-') && !customElements.get(name)) waits.push(customElements.whenDefined(name));
  }
  return waits;
};
const HOLD = 'a[href], button, summary, label, select, input[type="checkbox"], input[type="radio"], input[type="submit"], [role="button"], [data-quick-add]';
let heldTap = null;
document.addEventListener('click', (event) => {
  if (!event.isTrusted) return;
  const el = event.target.closest && event.target.closest(HOLD);
  if (!el || el.closest('[data-dialog-open], [data-dialog-close]')) return;
  // a plain link (real address, no behaviour attached) just navigates
  if (el.matches('a[href]') && !el.matches('[role="button"], [data-quick-add]') && !(el.getAttribute('href') || '').startsWith('#')
    && ![...el.attributes].some((a) => a.name.startsWith('data-')) && !el.closest(':not(:defined)')) return;
  const waits = tapNeeds(el);
  if (!waits.length) return;
  // a size / colour choice (radio or checkbox) changes at once, natively, so it shows at once; its scripts get the
  // change event as soon as they are there
  const choice = el.matches('input[type="radio"], input[type="checkbox"]') ? el : (el.matches('label') && el.control && el.control.matches('input[type="radio"], input[type="checkbox"]') ? el.control : null);
  if (choice) {
    startDeferred();
    Promise.all(waits).then(() => { if (choice.isConnected) choice.dispatchEvent(new Event('change', { bubbles: true })); });
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  heldTap = el;
  startDeferred();
  Promise.all(waits).then(() => { if (heldTap === el) { heldTap = null; if (el.isConnected) el.click(); } });
}, true);
['pointerdown', 'touchstart', 'keydown', 'focusin'].forEach((type) => document.addEventListener(type, startDeferred, { capture: true, passive: true, once: true }));
window.addEventListener('load', startDeferred, { once: true });
// run them as soon as the first contentful paint is on record (normally already true when this module runs)
const painted = () => performance.getEntriesByName('first-contentful-paint').length > 0;
const waitPaint = () => { if (painted()) startDeferred(); else requestAnimationFrame(waitPaint); };
if (document.visibilityState === 'visible') waitPaint();
else document.addEventListener('visibilitychange', startDeferred, { once: true });
document.addEventListener('shopify:section:load', (event) => {
  // theme editor: a re-rendered section brings its overlays back inside it; drop the copies the layout had moved below <main>
  event.target.querySelectorAll('dialog[id]').forEach((dialog) => {
    document.querySelectorAll(`dialog[id="${dialog.id}"]`).forEach((other) => { if (other !== dialog) other.remove(); });
  });
  if (deferStarted) Theme.runDeferred(event.target);
});

/* ---------- Early dialog taps (05 · CRO cold-load check, 2 Nov) ----------
   A tap on a [data-dialog-open] control before this file ran (menu, search, bag, "Find my size") is kept by the layout in
   window.__csPendingOpen. It is replayed here as a click, after the dialog, lazy-module and held-tap listeners are bound,
   so the opener does exactly what a later tap does: "Find my size" first loads cs-size-guide.js (cs-lazy hold) and then
   opens the dialog in the finder view, instead of the bare dialog in the guide view. If the replay doesn't open the
   dialog within 4 s (a failed script), it is opened directly. */
if (window.__csPendingOpen) {
  const pending = window.__csPendingOpen;
  window.__csPendingOpen = null;
  if (pending.isConnected) {
    const dialog = document.getElementById(pending.dataset.dialogOpen);
    pending.click();
    if (dialog) setTimeout(() => { if (!dialog.open && pending.isConnected) Theme.openDialog(dialog, pending); }, 4000);
  }
}

/* ---------- Accordions: ONE shared behaviour (round owner-12 A2; owner 5 Nov on the desktop PDP: "this expansion is
   again not smooth and the website just expands with it ... moving the whole page") ----------
   Every accordion of the theme runs through Theme.acc (base.css "Accordions" has the tokens, icon and focus ring):
   - <details> with ONE content element besides <summary> (PDP Details / Shipping & returns / Care / Questions band, FAQ
     page, contact questions, drop FAQ, legal picker, bag order note), found by itself; [data-acc-native] opts out;
   - a button + panel: <button data-acc-trigger aria-expanded="false" aria-controls="ID"> and <div id="ID"
     data-acc-panel hidden><div>content</div></div> (the panel has no padding / border of its own; spacing goes inside).
   Motion: the row's height goes from where it is to where it ends (measured, Web Animations), --cs-dur-acc (280 ms),
   --cs-ease-acc cubic-bezier(.2,.7,.2,1), the same both ways; the content fades in over the last 200 ms (out over the
   first 200 ms when closing). A <details> keeps [open] until its close has played; a click / Enter / Space during a
   motion turns it around from where it is. The clicked header keeps its place on screen: scroll anchoring is off
   while it moves and any shift of the page (or of the scrolling dialog it sits in) is scrolled back every frame.
   Reduced motion: the end state at once. Find in page / a #link into a closed row: the browser opens it (instantly).
   API: Theme.acc.toggle(el, open?), Theme.acc.open(el), Theme.acc.close(el), Theme.acc.isOpen(el); el = the <details>,
   its summary, a [data-acc-trigger] or its [data-acc-panel]. "cs-acc:toggle" { open } bubbles from the details / panel
   when a motion starts, "cs-acc:end" { open } when it has finished. Proof: tools/accordion_motion_gate.mjs. */
{
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(.2,.7,.2,1)';
  const FADE = 200;
  const runs = new WeakMap();
  const msOf = (el) => {
    const v = getComputedStyle(el).getPropertyValue('--cs-dur-acc').trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? (/ms$/.test(v) ? n : n * 1000) : 280;
  };
  const sum = (cs, ...k) => k.reduce((s, p) => s + (parseFloat(cs[p]) || 0), 0);
  // the parts of one accordion: root (the box whose height moves), trig (the header), fade (the content), kind
  const partsOf = (el) => {
    if (!el || el.nodeType !== 1) return null;
    const d = el.closest('details, [data-acc-trigger], [data-acc-panel]');
    if (d instanceof HTMLDetailsElement) {
      const trig = d.querySelector(':scope > summary');
      const kids = [...d.children].filter((c) => c !== trig);
      if (!trig || kids.length !== 1 || d.hasAttribute('data-acc-native') || d.hasAttribute('data-menu-disclosure')) return null;
      return { kind: 'details', root: d, trig, fade: kids[0] };
    }
    if (!d) return null;
    const trig = d.matches('[data-acc-trigger]') ? d : document.querySelector(`[data-acc-trigger][aria-controls="${CSS.escape(d.id)}"]`);
    const panel = trig && document.getElementById(trig.getAttribute('aria-controls'));
    return panel ? { kind: 'button', root: panel, trig, fade: panel.firstElementChild || panel } : null;
  };
  const stateOf = (p) => { const r = runs.get(p.root); if (r) return r.open; return p.kind === 'details' ? p.root.open : !p.root.hidden; };
  // the header's scroller: the nearest ancestor that scrolls (a drawer / dialog body), else the page
  const scrollerOf = (el) => {
    for (let n = el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
      const o = getComputedStyle(n).overflowY;
      if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight) return n;
    }
    return null;
  };
  // the closed height of a <details>: its summary and its own border / padding
  const closedH = (p) => {
    if (p.kind !== 'details') return 0;
    const cs = getComputedStyle(p.root); const ss = getComputedStyle(p.trig);
    return p.trig.getBoundingClientRect().height + sum(ss, 'marginTop', 'marginBottom') + sum(cs, 'borderTopWidth', 'borderBottomWidth', 'paddingTop', 'paddingBottom');
  };
  const emit = (p, type, open) => p.root.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { open } }));
  // slack: a row closing at the very end of a page / the bag's body would make it shorter than the screen it fills, and
  // the browser would pull everything down (the header with it). The lost height is lent back as bottom padding on the
  // scroller (the page: <body>) and given back only as it scrolls out of view, so nothing on screen moves for it.
  const slacks = new WeakMap();
  const lend = (sc, px) => {
    const box = sc || document.body;
    const view = sc || document.scrollingElement;
    const room = view.scrollHeight - view.scrollTop - view.clientHeight;
    const need = px - room;
    if (need <= 0.5) return;
    let s = slacks.get(box);
    if (!s) {
      s = { px: 0, pad: parseFloat(getComputedStyle(box).paddingBottom) || 0 };
      s.trim = () => {
        const end = view.scrollHeight - s.px;
        const shown = Math.max(0, view.scrollTop + view.clientHeight - end);
        if (shown >= s.px - 0.5) return;
        s.px = shown;
        if (shown <= 0.5) { box.style.removeProperty('padding-bottom'); slacks.delete(box); (sc || window).removeEventListener('scroll', s.trim); return; }
        box.style.setProperty('padding-bottom', `${s.pad + s.px}px`);
      };
      (sc || window).addEventListener('scroll', s.trim, { passive: true });
      slacks.set(box, s);
    }
    s.px += need;
    box.style.setProperty('padding-bottom', `${s.pad + s.px}px`);
  };
  const anchorOff = (sc, off) => { (sc ? [sc] : [document.documentElement, document.body]).forEach((n) => { n.style.overflowAnchor = off ? 'none' : ''; }); };
  // the end state, no motion
  const settle = (p, open) => {
    if (p.kind === 'details') { p.root.removeAttribute('data-acc-closing'); if (p.root.open !== open) p.root.open = open; }
    else { p.root.hidden = !open; p.trig.setAttribute('aria-expanded', String(open)); }
  };
  const finish = (p, r) => {
    if (runs.get(p.root) !== r) return;
    runs.delete(p.root);
    cancelAnimationFrame(r.raf);
    settle(p, r.open);
    r.anim.cancel(); r.fadeAnim.cancel();
    p.root.removeAttribute('data-acc-anim');
    anchorOff(r.sc, false);
    emit(p, 'cs-acc:end', r.open);
  };
  const set = (el, open) => {
    const p = partsOf(el);
    if (!p) return false;
    const was = stateOf(p);
    open = open === undefined ? !was : !!open;
    const prev = runs.get(p.root);
    if (!prev && was === open) return true;
    if (prev && prev.open === open) return true;
    // where it is now (mid-motion included) and how see-through the content is
    const from = p.root.getBoundingClientRect().height;
    const fromO = prev ? parseFloat(getComputedStyle(p.fade).opacity) : (open ? 0 : 1);
    if (prev) { runs.delete(p.root); cancelAnimationFrame(prev.raf); prev.anim.cancel(); prev.fadeAnim.cancel(); anchorOff(prev.sc, false); }
    const instant = still.matches || typeof p.root.animate !== 'function';
    // the end state's markup first (it is measured): open = [open] / no [hidden]; close = the icon turns back now
    if (open) { if (p.kind === 'details') { p.root.removeAttribute('data-acc-closing'); p.root.open = true; } else { p.root.hidden = false; p.trig.setAttribute('aria-expanded', 'true'); } }
    else if (p.kind === 'details') p.root.setAttribute('data-acc-closing', '');
    else p.trig.setAttribute('aria-expanded', 'false');
    const shut = closedH(p);
    if (instant) {
      // reduced motion: the end state at once; a close at the very end of the page / bag still lends its height back
      if (!open && from > shut) lend(scrollerOf(p.root), from - shut);
      settle(p, open); p.root.removeAttribute('data-acc-anim'); emit(p, 'cs-acc:toggle', open); emit(p, 'cs-acc:end', open); return true;
    }
    const full = (open ? p.root.getBoundingClientRect().height : shut + p.fade.getBoundingClientRect().height + sum(getComputedStyle(p.fade), 'marginTop', 'marginBottom'));
    const to = open ? full : shut;
    const travel = Math.abs(full - shut) || 1;
    const base = msOf(p.root);
    // a turn-around mid-motion takes the part of the time that is left to go (at least 40%)
    const dur = Math.round(base * Math.min(1, Math.max(prev ? 0.4 : 1, Math.abs(to - from) / travel)));
    const fd = Math.min(FADE, dur);
    const sc = scrollerOf(p.root);
    const y0 = p.trig.getBoundingClientRect().top;
    anchorOff(sc, true);
    if (to < from) lend(sc, from - to);
    p.root.setAttribute('data-acc-anim', '');
    const anim = p.root.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: dur, easing: EASE });
    const fadeAnim = open
      ? p.fade.animate([{ opacity: fromO }, { opacity: 1 }], { duration: fd, delay: prev ? 0 : dur - fd, easing: 'linear', fill: 'both' })
      : p.fade.animate([{ opacity: fromO }, { opacity: 0 }], { duration: fd, easing: 'linear', fill: 'both' });
    const r = { open, anim, fadeAnim, sc, raf: 0 };
    runs.set(p.root, r);
    // keep the header where it was: undo any shift of the page / dialog every frame (a browser clamp at the very end of a
    // page is the only thing left: there is nothing below to scroll to)
    const hold = () => {
      if (runs.get(p.root) !== r) return;
      const dy = p.trig.getBoundingClientRect().top - y0;
      if (Math.abs(dy) > 0.25) (sc || window).scrollTo({ top: (sc ? sc.scrollTop : window.scrollY) + dy, behavior: 'instant' });
      r.raf = requestAnimationFrame(hold);
    };
    r.raf = requestAnimationFrame(hold);
    anim.onfinish = () => finish(p, r);
    emit(p, 'cs-acc:toggle', open);
    return true;
  };
  Theme.acc = { toggle: (el, open) => set(el, open), open: (el) => set(el, true), close: (el) => set(el, false), isOpen: (el) => { const p = partsOf(el); return !!p && stateOf(p); } };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !event.target.closest) return;
    const trig = event.target.closest('summary, [data-acc-trigger]');
    if (!trig) return;
    // a link / control inside the header does its own thing
    const control = event.target.closest('a[href], button, input, select, textarea, label');
    if (control && control !== trig && trig.contains(control)) return;
    const p = partsOf(trig);
    if (!p || p.trig !== trig) return;
    event.preventDefault();
    set(trig);
  });
  // the browser (find in page, a #link into a closed row) or other code changed [open] itself: stop the motion, keep that
  document.addEventListener('toggle', (event) => {
    const d = event.target;
    const r = d instanceof HTMLDetailsElement && runs.get(d);
    if (!r || (event.newState === 'open') === r.open || (r.open === false && d.open)) return;
    runs.delete(d); cancelAnimationFrame(r.raf); r.anim.cancel(); r.fadeAnim.cancel();
    d.removeAttribute('data-acc-anim'); d.removeAttribute('data-acc-closing'); anchorOff(r.sc, false);
  }, true);
  // focus rings only after keyboard use (base.css: summaries and [data-acc-trigger]; component-product.css: options); the
  // same tracker as component-product.js, bound once by whichever runs first
  if (!window.__csInputModality) {
    window.__csInputModality = true;
    const html = document.documentElement;
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' || event.key.startsWith('Arrow') || event.key === ' ' || event.key === 'Enter') html.dataset.csInput = 'key';
    }, true);
    document.addEventListener('pointerdown', () => { if (html.dataset.csInput === 'key') html.dataset.csInput = 'pointer'; }, true);
  }
}

/* Press state on iOS (round owner-9 A, owner QA D-11): Safari only applies :active while the page listens to touchstart.
   The deferred-script listener above is `once`, so after the first touch the press state (base.css "Press state")
   stopped showing. One passive, empty listener keeps it on. */
document.addEventListener('touchstart', () => {}, { passive: true });

export default Theme;
